import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import WeddingsPage from "@/pages/WeddingsPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPricingRules from "@/pages/AdminPricingRules";
import AdminVenuePricing from "@/pages/AdminVenuePricing";
import AdminFoodPricing from "@/pages/AdminFoodPricing";
import AdminDecorCatalog from "@/pages/AdminDecorCatalog";
import AdminLeads from "@/pages/AdminLeads";
import AdminLockedDeals from "@/pages/AdminLockedDeals";
import AdminContentManager from "@/pages/AdminContentManager";
import AdminAvailabilityCalendar from "@/pages/AdminAvailabilityCalendar";
import AdminAvailabilityActivity from "@/pages/AdminAvailabilityActivity";
import AdminAnalyticsDashboard from "@/pages/AdminAnalyticsDashboard";
import AdminChatManager from "@/pages/AdminChatManager";
import AdminLiveActivity from "@/pages/AdminLiveActivity";
import AdminCustomerManager from "@/pages/AdminCustomerManager";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Customer Dashboard imports (isolated module)
import CustomerLogin from "@/pages/customer/CustomerLogin";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import FoodMenuPage from "@/pages/customer/FoodMenuPage";
import MealPlanningPage from "@/pages/customer/MealPlanningPage";
import { 
  RoomAllocationPage, 
  ItineraryPage, 
  InvitePageBuilder, 
  DecorPage 
} from "@/pages/customer/ModulePlaceholder";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-shimmer w-16 h-16 rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Navigate to="/weddings" replace />} />
            <Route path="/weddings" element={<WeddingsPage />} />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/pricing-rules" element={
              <ProtectedRoute>
                <AdminPricingRules />
              </ProtectedRoute>
            } />
            <Route path="/admin/venue-pricing" element={
              <ProtectedRoute>
                <AdminVenuePricing />
              </ProtectedRoute>
            } />
            <Route path="/admin/food-pricing" element={
              <ProtectedRoute>
                <AdminFoodPricing />
              </ProtectedRoute>
            } />
            <Route path="/admin/decor-catalog" element={
              <ProtectedRoute>
                <AdminDecorCatalog />
              </ProtectedRoute>
            } />
            <Route path="/admin/leads" element={
              <ProtectedRoute>
                <AdminLeads />
              </ProtectedRoute>
            } />
            <Route path="/admin/locked-deals" element={
              <ProtectedRoute>
                <AdminLockedDeals />
              </ProtectedRoute>
            } />
            <Route path="/admin/content" element={
              <ProtectedRoute>
                <AdminContentManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/availability" element={
              <ProtectedRoute>
                <AdminAvailabilityCalendar />
              </ProtectedRoute>
            } />
            <Route path="/admin/availability-activity" element={
              <ProtectedRoute>
                <AdminAvailabilityActivity />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute>
                <AdminAnalyticsDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/chats" element={
              <ProtectedRoute>
                <AdminChatManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/live" element={
              <ProtectedRoute>
                <AdminLiveActivity />
              </ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
              <ProtectedRoute>
                <AdminCustomerManager />
              </ProtectedRoute>
            } />
            
            {/* Customer Dashboard Routes (Isolated Module) */}
            <Route path="/my-wedding" element={<CustomerLogin />} />
            <Route path="/my-wedding/dashboard" element={<CustomerDashboard />} />
            <Route path="/my-wedding/food-menu" element={<FoodMenuPage />} />
            <Route path="/my-wedding/food-menu/:day/:mealType" element={<MealPlanningPage />} />
            <Route path="/my-wedding/rooms" element={<RoomAllocationPage />} />
            <Route path="/my-wedding/itinerary" element={<ItineraryPage />} />
            <Route path="/my-wedding/invite" element={<InvitePageBuilder />} />
            <Route path="/my-wedding/decor" element={<DecorPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
